import { useEffect, useRef, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';

/**
 * The one place the whole application says "something is on its way": a thin bar across the
 * top while a page is loading, a list is being re-read or a change is being saved.
 *
 * It waits a moment before showing itself, because a bar that flashes on every 200 ms read
 * is noise rather than news, and it stays up for a moment once it appears so it never
 * blinks in and out.
 */
const SHOW_AFTER_MS = 250;
const KEEP_FOR_MS = 400;

export function ActivityBar() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const routing = useRouterState({ select: (state) => state.status === 'pending' });
  const busy = fetching + mutating > 0 || routing;
  const [visible, setVisible] = useState(false);
  const shownAt = useRef(0);

  useEffect(() => {
    if (busy) {
      const timer = window.setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, SHOW_AFTER_MS);
      return () => window.clearTimeout(timer);
    }
    if (!visible) return;
    const left = Math.max(0, KEEP_FOR_MS - (Date.now() - shownAt.current));
    const timer = window.setTimeout(() => setVisible(false), left);
    return () => window.clearTimeout(timer);
  }, [busy, visible]);

  return (
    <div
      aria-hidden={!visible}
      className={cnBar(visible)}
      role="presentation"
      data-testid="activity-bar"
    >
      <span className="ts-activity-bar__glow" />
    </div>
  );
}

function cnBar(visible: boolean): string {
  return `ts-activity-bar${visible ? ' ts-activity-bar--on' : ''}`;
}

/**
 * The same signal in words, for a screen reader: announced politely, so somebody who cannot
 * see the bar still knows the page is working.
 */
export function ActivityAnnouncer() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const busy = fetching + mutating > 0;
  const [message, setMessage] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setMessage(busy ? 'Loading' : ''), busy ? 600 : 0);
    return () => window.clearTimeout(timer);
  }, [busy]);

  return (
    <span aria-live="polite" className="sr-only">
      {message}
    </span>
  );
}
